import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ConfigurationService } from '../../../../../services/configuration.service';
import { IExtendedProjectInDTO } from '../../../../../models/project/project-model';
import { FunctionsHelperService, timeAgo } from '../../../../../services/functions-helper.service';
import { Router } from '@angular/router';
import { IExtendedLayout, ILayout, ProjectRecordingTypeEnum, ProjectStatusEnum } from 'src/app/models/defines';
import { DynamicItemType, IDynamicLottieChange } from 'lottie-json-helper/lib/types';
import { ArtDirectorService } from 'src/app/services/art-director.service';

import { LottiePlayerService } from 'src/app/services/lottie-player.service';
import { AnimationItem } from 'lottie-web';
import { v4 as uuidv4 } from 'uuid';
import { EditJobStatusEnum } from '../../../../../models/job/edit-job-schema';
import { ProjectCardService } from '../../../../../services/project-card.service';

@Component({
    selector: 'project-card',
    templateUrl: './project-card.component.html',
    styleUrls: ['./project-card.component.scss'],
})
export class ProjectCardComponent implements OnInit, OnDestroy {
    ProjectStatusEnum = ProjectStatusEnum;
    personalizedDynamics: IDynamicLottieChange[];
    animationItems = new Map<string, AnimationItem>();

    project: IExtendedProjectInDTO;

    @Input({ alias: 'project', required: true })
    set projectSet(val: IExtendedProjectInDTO) {
        this.project = val;
        if (!this.project) {
            return;
        }
        this.toDisplayTrialBanner = this.projectCardService.getProjectToDisplayBanner(this.project);
        const id = uuidv4();
        const iconLayout: IExtendedLayout = {
            _id: id,
            lottiePath: this.project.icon,
            relativeLottiePath: this.project.icon,
            baseDesignPath: '',
        };
        this.project.iconLayout = iconLayout;
    }
    @Input('minimal') minimal: boolean = false;
    @Input('usedFromHomepage') showOnlyStatus: boolean = false;

    toDisplayTrialBanner = false;
    projectExportPercentage = 0;
    exportInterval: any;
    formattedCreationTime: string;
    ProjectRecordingTypeEnum = ProjectRecordingTypeEnum;

    constructor(
        private readonly router: Router,
        public config: ConfigurationService,
        public helperFunctions: FunctionsHelperService,
        private artDirector: ArtDirectorService,
        private lottiePlayer: LottiePlayerService,
        private projectCardService: ProjectCardService
    ) {}

    ngOnInit(): void {
        const changesFromBranding = this.artDirector.extractDynamicLottieByProjectBranding(this.project);
        if (this.project?.shortTitle) {
            changesFromBranding.push({
                id: DynamicItemType.HEADER + 0,
                type: DynamicItemType.HEADER,
                position: 0,
                value: this.project.shortTitle,
                placeholder: '',
                font: null,
                originalFontId: '',
            });
        }

        this.personalizedDynamics = changesFromBranding;
        this.formattedCreationTime = timeAgo(this.project.createdAt);
        if (this.project.statuses.general === ProjectStatusEnum.EXPORTING) {
            const edit = this.findWorkingEdit(this.project);
            if (edit) {
                const now = Date.now();
                this.projectExportPercentage = this.getExportPercentage(new Date(edit.createdAt), now);
                if (this.projectExportPercentage >= 90) {
                    return;
                }
                // Start an interval that updates the percentage every 5 seconds
                this.exportInterval = setInterval(() => {
                    const now = Date.now(); // Get real current time
                    this.projectExportPercentage = this.getExportPercentage(new Date(edit.createdAt), now);

                    // Stop the interval if the progress reaches 90%
                    if (this.projectExportPercentage >= 90) {
                        clearInterval(this.exportInterval);
                    }
                }, 2000); // Run every 2 seconds
            }
        }
    }

    private getExportPercentage(editCreatedAt: Date, currentTime: number): number {
        const createdAtTimestamp = editCreatedAt.getTime();
        const elapsedSeconds = Math.floor((currentTime - createdAtTimestamp) / 1000);

        // If more than 540 seconds (9 minutes) have passed, return 90%
        if (elapsedSeconds >= 540) {
            return 90;
        }

        // Base percentage (starts at 5%)
        let percentage = 5 + elapsedSeconds / 6; // 1% increase every ~6 seconds

        // Add a small variation factor to simulate a real export
        const variation = Math.sin(elapsedSeconds / 10) * 2; // Sin wave variation (±2%)

        percentage += variation;

        // Ensure percentage is always increasing and does not exceed 90%
        percentage = Math.min(Math.max(percentage, 5), 90);

        return Math.floor(percentage); // ✅ Now it always returns a whole number
    }

    findWorkingEdit(project: IExtendedProjectInDTO) {
        return project.edits.filter((edit) => edit.status !== EditJobStatusEnum.DUMMY).pop();
    }

    playAnimation(id: string) {
        const animationItem = this.animationItems.get(id);
        if (!animationItem) {
            return;
        }

        this.lottiePlayer.playAnimation(animationItem);
    }

    pauseAnimation(id: string) {
        const animationItem = this.animationItems.get(id);
        if (!animationItem) {
            return;
        }

        this.lottiePlayer.goToAndPause(animationItem, 1);
    }

    public animationCreated(anmimationItem: AnimationItem, id: string) {
        this.animationItems.set(id, anmimationItem);
    }

    ngOnDestroy(): void {
        if (this.exportInterval) {
            clearInterval(this.exportInterval); // Stop interval when component is destroyed
        }
    }
}
